/**
 * Formations (instrumentation uniquement) et Genres pour les exercices.
 * Formation = instrumentation (piano, orchestre, etc.) — peut être multiple (ex. piano + orchestre pour concerto).
 * Genre = type d'œuvre (concerto, trio, symphonie, etc.).
 */

import { CLASSICAL_FORMATIONS } from './classicalInstruments'

/** Instrumentation — musique classique (liste maître : instruments + effectifs) */
export { CLASSICAL_FORMATIONS }

/** Instrumentation — Nouveaux Horizons */
export const HORIZONS_FORMATIONS = [
  { id: 'piano_clavier', label: 'Piano / clavier' },
  { id: 'chant', label: 'Chant' },
  { id: 'guitare', label: 'Guitare' },
  { id: 'groupe_band', label: 'Groupe / band (rock, pop)' },
  { id: 'big_band', label: 'Big band' },
  { id: 'orchestre', label: 'Orchestre (BO, symphonique)' },
  { id: 'orchestre_chant', label: 'Orchestre + chant / chœur' },
  { id: 'electronique_synthe', label: 'Électronique / synthé' },
  { id: 'combo_jazz', label: 'Combo jazz' },
  { id: 'petit_ensemble', label: 'Petit ensemble' },
  { id: 'autre', label: 'Autre' }
]

/** Genres (type d'œuvre) — classique : concerto, trio, symphonie, etc. */
export const CLASSICAL_GENRES = [
  { id: 'aria', label: 'Aria' },
  { id: 'autre', label: 'Autre' },
  { id: 'ballade', label: 'Ballade' },
  { id: 'cantate', label: 'Cantate' },
  { id: 'caprice', label: 'Caprice' },
  { id: 'concerto', label: 'Concerto' },
  { id: 'double_concerto', label: 'Double concerto' },
  { id: 'divertimento', label: 'Divertimento' },
  { id: 'duo', label: 'Duo' },
  { id: 'etude', label: 'Étude' },
  { id: 'fantaisie', label: 'Fantaisie' },
  { id: 'fugue', label: 'Fugue' },
  { id: 'lied_melodie', label: 'Lied / Mélodie' },
  { id: 'messe', label: 'Messe' },
  { id: 'motet', label: 'Motet' },
  { id: 'nocturne', label: 'Nocturne' },
  { id: 'nonette', label: 'Nonette' },
  { id: 'opera', label: 'Opéra' },
  { id: 'oratorio', label: 'Oratorio' },
  { id: 'ouverture', label: 'Ouverture' },
  { id: 'partita', label: 'Partita' },
  { id: 'passacaille_ou_chaconne', label: 'Passacaille / Chaconne' },
  { id: 'poeme_symphonique', label: 'Poème symphonique' },
  { id: 'prelude', label: 'Prélude' },
  { id: 'quatuor_cordes', label: 'Quatuor à cordes' },
  { id: 'quatuor_piano', label: 'Quatuor avec piano' },
  { id: 'quintette', label: 'Quintette' },
  { id: 'requiem', label: 'Requiem' },
  { id: 'rhapsodie', label: 'Rhapsodie' },
  { id: 'serenade', label: 'Sérénade' },
  { id: 'sextuor_septuor_octuor', label: 'Sextuor, septuor, octuor' },
  { id: 'sinfonia_concertante', label: 'Sinfonia concertante' },
  { id: 'sonate', label: 'Sonate' },
  { id: 'song', label: 'Song' },
  { id: 'suite', label: 'Suite' },
  { id: 'symphonie', label: 'Symphonie' },
  { id: 'toccata', label: 'Toccata' },
  { id: 'trio', label: 'Trio' },
  { id: 'variations', label: 'Variations' }
]

/** @type {Set<string>} */
const CLASSICAL_GENRE_ID_SET = new Set(CLASSICAL_GENRES.map((g) => g.id))

/**
 * Genres inférés depuis le titre qui impliquent aussi la formation « musique de chambre »
 * (aligné sur inferGenreFromWorkTitle).
 */
export const CHAMBER_ENSEMBLE_GENRE_IDS = new Set([
  'duo',
  'trio',
  'quatuor_cordes',
  'quatuor_piano',
  'quintette',
  'sextuor_septuor_octuor',
  'nonette',
  'sinfonia_concertante',
  'divertimento',
  'serenade'
])

const SONATE_CHAMBRE_INSTRUMENT_RE =
  /\b(violon|violin|violoncelle|cello|alto|flûte|flute|hautbois|clarinette|basson|trompette|duo|pour\s+(violon|alto|violoncelle|flûte|flute)|with\s+violin|for\s+violin|for\s+cello)\b/i

/**
 * Quatuor à cordes dans le titre (FR/EN/DE/IT/ES).
 * Important : `\bquartet\b` ne matche pas « quartett » (double t) — frontière de mot après le 7e caractère.
 */
export const RE_TITRE_QUATUOR_CORDES =
  /\b(?:string\s+quartett?|streich[- ]?quartett?|streichquartett|quatuor(?:\s+(?:à|a)\s*cordes)?|quartett?|quartette|quartetto|cuarteto)\b/i

const FORMATION_TAG_PREFIX = 'Formation'
const GENRE_TAG_PREFIX = 'Genre'

/**
 * Tag autoTags pour une formation (instrumentation).
 */
export function getFormationTag(formationId) {
  if (!formationId) return null
  const parts = formationId.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
  return FORMATION_TAG_PREFIX + parts.join('')
}

/**
 * Tag autoTags pour un genre.
 */
export function getGenreTag(genreId) {
  if (!genreId) return null
  const parts = genreId.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
  return GENRE_TAG_PREFIX + parts.join('')
}

/** Toutes les formations (classique + Horizons) pour la résolution tag → label */
const ALL_FORMATIONS = [...CLASSICAL_FORMATIONS, ...HORIZONS_FORMATIONS]

/**
 * Retourne le libellé d'une formation à partir du tag autoTags (ex. "FormationPiano" → "Piano").
 * @param {string} tag - Tag tel que stocké dans autoTags
 * @returns {string|null}
 */
export function getFormationLabelFromTag(tag) {
  if (!tag || typeof tag !== 'string' || !tag.startsWith(FORMATION_TAG_PREFIX)) return null
  const found = ALL_FORMATIONS.find((f) => getFormationTag(f.id) === tag)
  return found ? found.label : null
}

/**
 * Retourne le libellé d'un genre à partir du tag autoTags (ex. "GenreSymphonie" → "Symphonie").
 * @param {string} tag - Tag tel que stocké dans autoTags
 * @returns {string|null}
 */
export function getGenreLabelFromTag(tag) {
  if (!tag || typeof tag !== 'string' || !tag.startsWith(GENRE_TAG_PREFIX)) return null
  const found = CLASSICAL_GENRES.find((g) => getGenreTag(g.id) === tag)
  return found ? found.label : null
}

/**
 * Infère les instrumentations depuis le titre (pour suggestion / rétrocompat).
 * Retourne un tableau d'ids (ex. concerto → ['piano', 'orchestre_symphonique']).
 *
 * @param {{ storedGenreId?: string|null }} [options] - Si `storedGenreId` est un id genre classique
 *   enregistré (metadata), il prime sur l’inférence genre depuis le titre pour la liaison
 *   « genres chambre → musique_de_chambre » (recalcul autoTags / cohérence avec correction manuelle).
 */
export function inferFormationsFromWorkTitle(workTitle, section = null, options = {}) {
  if (!workTitle || typeof workTitle !== 'string') return []
  const t = workTitle.toLowerCase().trim()
  const result = []
  if (section === 'horizons') {
    if (/\borchestre\b|\bsymphonique\b|\bbo\b|\bsoundtrack\b/.test(t)) result.push('orchestre')
    if (/\bbig\s*band\b|\bjazz\s*orchestra\b/.test(t)) result.push('big_band')
    if (/\bchant\b.*\bpiano\b|\bpiano\b.*\bchant\b|\bballad\b/.test(t)) { result.push('chant'); result.push('piano_clavier') }
    if (/\bchant\b.*\bguitar|\bguitar\b.*\bchant\b|\bacoustic\b/.test(t)) { result.push('chant'); result.push('guitare') }
    if (/\bband\b|\bgroupe\b|\brock\b|\bpop\b/.test(t)) result.push('groupe_band')
    if (/\belectronique\b|\bsynth\b|\bedm\b|\bsynthwave\b|\bchiptune\b/.test(t)) result.push('electronique_synthe')
    if (/\bcombo\b|\bjazz\s*trio\b|\btrio\s*jazz\b/.test(t)) result.push('combo_jazz')
    return [...new Set(result)]
  }
  // Classique : instrumentation uniquement
  if (/\bmusique\s+de\s+chambre\b|\bchamber\s+music\b/.test(t)) result.push('musique_de_chambre')
  if (/\bsinfonia\s+concertante\b/i.test(t)) result.push('musique_de_chambre')
  if (/\bsymphonie\b|\bsymphony\b|\bsinfonie\b/.test(t)) result.push('orchestre_symphonique')
  if (/\bsinfonia\b/.test(t) && !/\bsinfonia\s+concertante\b/i.test(t)) result.push('orchestre_symphonique')
  if (/\bconcerto\b/.test(t)) { result.push('orchestre_symphonique'); result.push('piano') } // par défaut concerto piano
  if (/\bduo\b|\bduet\b|\bduetto\b/.test(t)) result.push('musique_de_chambre')
  if (RE_TITRE_QUATUOR_CORDES.test(t)) result.push('musique_de_chambre')
  if (/\btrio\b/.test(t)) { result.push('musique_de_chambre'); result.push('piano') } // trio avec piano souvent
  if (/\bquintette\b|\bquintet\b|\bquintett\b/.test(t)) result.push('musique_de_chambre')
  if (/\bnonette\b|\bnonet\b/.test(t)) result.push('musique_de_chambre')
  if (/\blied\b|\bmélodie\b|\bmelodie\b|\bsong\s*cycle\b/.test(t)) { result.push('chant'); result.push('piano') }
  if (/\bsonate\b|\ssonata\b/.test(t) && (/\bpiano\b|\bclavecin\b|\bkeyboard\b/.test(t) || !/\bviolon\b|\bviolin\b|\bcello\b|\bflute\b/.test(t))) result.push('piano')
  if (/\bprélude\b|\bprelude\b|\bnocturne\b|\bballade\b|\bétude\b|\betude\b/.test(t) && /\bpiano\b|^piano\s/i.test(workTitle)) result.push('piano')
  if (/\bopéra\b|\bopera\b|\bop\.\s*\d/.test(t)) { result.push('chant'); result.push('orchestre_symphonique') }
  if (/\bchœur\b|\bchoir\b|\bchorus\b/.test(t)) result.push('choeur')
  if (/\bviolon\b|\bviolin\b/.test(t) && !RE_TITRE_QUATUOR_CORDES.test(t) && !/\btrio\b|\bduo\b/.test(t)) result.push('violon')
  if (/\bclavecin\b|\bharpsichord\b/.test(t)) result.push('clavecin')
  if (/\borgue\b|\borgan\b/.test(t)) result.push('orgue')
  if (/\bvioloncelle\b|\bcello\b/.test(t) && !RE_TITRE_QUATUOR_CORDES.test(t) && !/\btrio\b|\bduo\b/.test(t)) result.push('violoncelle')
  if (/\bflûte\b|\bflute\b/.test(t) && !RE_TITRE_QUATUOR_CORDES.test(t)) result.push('flute')

  const stored = options.storedGenreId
  const genreForCoupling =
    stored && CLASSICAL_GENRE_ID_SET.has(stored)
      ? stored
      : inferGenreFromWorkTitle(workTitle)
  if (genreForCoupling && CHAMBER_ENSEMBLE_GENRE_IDS.has(genreForCoupling)) {
    result.push('musique_de_chambre')
  }
  if (genreForCoupling === 'sonate' && SONATE_CHAMBRE_INSTRUMENT_RE.test(workTitle)) {
    result.push('musique_de_chambre')
  }

  return [...new Set(result)]
}

/**
 * Inférence genre depuis le titre (pour suggestion / rétrocompat).
 */
export function inferGenreFromWorkTitle(workTitle) {
  if (!workTitle || typeof workTitle !== 'string') return null
  const t = workTitle.toLowerCase().trim()
  let out = null
  if (/\bsinfonia\s+concertante\b/i.test(t)) out = 'sinfonia_concertante'
  else if (/\bsymphonie\b|\bsymphony\b|\bsinfonia\b|\bsinfonie\b/.test(t)) out = 'symphonie'
  else if (/\bdouble\s*concerto\b|\bdouble\s*concert\b/.test(t)) out = 'double_concerto'
  else if (/\bconcerto\b/.test(t)) out = 'concerto'
  else if (/\bduo\b|\bduet\b|\bduetto\b/.test(t)) out = 'duo'
  else if (/\bpiano\s*quartett?\b|\bklavierquartett?\b|\bquatuor\s+avec\s+piano\b|\bquatuor\s+piano\b/i.test(t)) out = 'quatuor_piano'
  else if (RE_TITRE_QUATUOR_CORDES.test(t)) out = 'quatuor_cordes'
  else if (/\btrio\b/.test(t)) out = 'trio'
  else if (/\bnonette\b|\bnonet\b/.test(t)) out = 'nonette'
  else if (/\bquintette\b|\bquintet\b|\bquintett\b/.test(t)) out = 'quintette'
  else if (/\blied\b|\bmélodie\b|\bmelodie\b|\bsong\s*cycle\b/.test(t)) out = 'lied_melodie'
  else if (/\baria\b|\barias\b/.test(t)) out = 'aria'
  else if (/\bchanson\b|\bsong\b(?!\s*cycle)/.test(t)) out = 'song'
  else if (/\bsonate\b|\ssonata\b/.test(t)) out = 'sonate'
  else if (/\bopéra\b|\bopera\b/.test(t)) out = 'opera'
  else if (/\boratorio\b|\boratorium\b/.test(t)) out = 'oratorio'
  else if (/\bouverture\b|\boverture\b/.test(t)) out = 'ouverture'
  else if (/\bpo[eè]me\s*symphonique\b|\bsymphonic\s*poem\b/.test(t)) out = 'poeme_symphonique'
  else if (/\bprélude\b|\bprelude\b/.test(t)) out = 'prelude'
  else if (/\bnocturne\b/.test(t)) out = 'nocturne'
  else if (/\bballade\b|\bballad\b/.test(t)) out = 'ballade'
  else if (/\bcantate\b|\bcantata\b/.test(t)) out = 'cantate'
  else if (/\bdivertimento\b/.test(t)) out = 'divertimento'
  else if (/\bpartita\b/.test(t)) out = 'partita'
  else if (/\bcaprice\b|\bcapriccio\b/.test(t)) out = 'caprice'
  else if (/\bvariation(s)?\b/.test(t)) out = 'variations'
  else if (/\bpassacaille\b|\bpassacaglia\b|\bchaconne\b/.test(t)) out = 'passacaille_ou_chaconne'
  else if (/\bétude\b|\betude\b|\bstudy\b/.test(t)) out = 'etude'
  else if (/\bfantaisie\b|\bfantasia\b|\bfantasy\b/.test(t)) out = 'fantaisie'
  else if (/\bfugue\b/.test(t)) out = 'fugue'
  else if (/\bmesse\b|\bmass\b/.test(t)) out = 'messe'
  else if (/\bmotet\b/.test(t)) out = 'motet'
  else if (/\brequiem\b/.test(t)) out = 'requiem'
  else if (/\brhapsodie\b|\brhapsody\b/.test(t)) out = 'rhapsodie'
  else if (/\bsérénade\b|\bserenade\b/.test(t)) out = 'serenade'
  else if (/\bsuite\b/.test(t)) out = 'suite'
  else if (/\btoccata\b/.test(t)) out = 'toccata'
  else if (/\bsextuor\b|\bseptuor\b|\boctuor\b|\bsextet\b|\boctet\b/.test(t)) out = 'sextuor_septuor_octuor'
  return out
}

/**
 * Fusionne les ids formation du référentiel / Firestore avec l'inférence depuis le titre.
 * Corrige notamment : genreId = quatuor_cordes mais formationIds = ['autre'] (données œuvres ou anciennes sauvegardes).
 *
 * @param {string[]|null|undefined} referenceIds
 * @param {string} workTitle
 * @param {string|null} section - 'horizons' ou null (classique)
 * @param {string|null|undefined} genreId - metadata.genre ; si absent, déduit du titre
 * @param {(id: string) => boolean} isValidFormationId
 * @returns {string[]}
 */
export function mergeFormationIdsFromReferenceAndTitle(
  referenceIds,
  workTitle,
  section,
  genreId,
  isValidFormationId
) {
  const ref = (Array.isArray(referenceIds) ? referenceIds : referenceIds ? [referenceIds] : []).filter(
    isValidFormationId
  )
  const wt = workTitle || ''
  const inferOpts =
    genreId && CLASSICAL_GENRE_ID_SET.has(genreId) ? { storedGenreId: genreId } : {}
  const inferred = inferFormationsFromWorkTitle(wt, section, inferOpts).filter(isValidFormationId)
  const genreResolved = genreId || inferGenreFromWorkTitle(wt)

  const onlyPlaceholder = ref.length > 0 && ref.every((id) => id === 'autre' || id === 'autre_solo')
  if (onlyPlaceholder && inferred.length > 0) {
    return [...new Set(inferred)]
  }

  const chamberGenre = genreResolved && CHAMBER_ENSEMBLE_GENRE_IDS.has(genreResolved)
  if (chamberGenre && ref.includes('autre')) {
    const stripped = ref.filter((id) => id !== 'autre' && id !== 'autre_solo')
    return [...new Set([...stripped, ...inferred])]
  }

  if (ref.length === 0 && inferred.length > 0) {
    return [...new Set(inferred)]
  }

  return [...new Set([...ref, ...inferred])]
}

/** @deprecated Utiliser inferFormationsFromWorkTitle (retourne un tableau). Gardé pour compat. */
export function inferFormationFromWorkTitle(workTitle, section = null) {
  const arr = inferFormationsFromWorkTitle(workTitle, section)
  return arr.length > 0 ? arr[0] : null
}
